import { useState, useEffect, useMemo } from "react";
import { C, fmt } from "../styles/tokens";
import { achatsService } from "../api/finances";
import { productsService, movementsService, unitesService } from "../api/stock";
import { Card, Badge, Btn, Modal, Input, Empty, Spinner } from "../components/ui";
import { handleApiError } from "../hooks/index";
import { unwrap } from "../api/client";

/**
 * Gestion achat = Achats fournisseurs valorisés + création de produits.
 * Workflow de validation :
 *   - Manager / Admin : l'achat est validé immédiatement (stock + dépense).
 *   - Gérant : l'achat part EN ATTENTE ; un Manager doit le valider pour
 *     que le stock et la dépense soient appliqués.
 */
const StockEntriesScreen = ({ products, setProducts, setMovements, role, toast }) => {
  const roleLc = (role || "").toLowerCase();
  const isManagerOrAdmin = ["manager", "admin", "administrateur"].includes(roleLc);

  const [achats, setAchats] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [expanded, setExpanded] = useState(null);

  // Formulaire achat
  const [showForm, setShowForm] = useState(false);
  const [fournisseur, setFournisseur] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]); // { id, nom, unite, qte, pu }
  const [loading, setLoading] = useState(false);

  // Combobox produit (menu déroulant avec recherche)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Modale création produit
  const EMPTY_PROD = { nom: "", categorie: "", unite_id: "", seuil_alerte: "", date_peremption: "", description: "" };
  const [showProd, setShowProd] = useState(false);
  const [prod, setProd] = useState(EMPTY_PROD);
  const [unites, setUnites] = useState([]);
  const pf = (v) => setProd(prev => ({ ...prev, ...v }));

  useEffect(() => {
    achatsService.list()
      .then(d => setAchats(Array.isArray(d) ? d : (d?.results ?? [])))
      .catch(() => {})
      .finally(() => setFetching(false));
    unitesService.list()
      .then(d => setUnites(Array.isArray(d) ? d : (d?.results ?? [])))
      .catch(() => {});
  }, []);

  const total = useMemo(
    () => items.reduce((s, i) => s + (Number(i.qte) || 0) * (Number(i.pu) || 0), 0),
    [items]
  );

  // Produits sélectionnables : non déjà ajoutés + correspondant à la recherche
  const availableProducts = useMemo(() => {
    const chosen = new Set(items.map(i => i.id));
    const q = search.trim().toLowerCase();
    return products
      .filter(p => !chosen.has(p.id))
      .filter(p => !q || p.nom.toLowerCase().includes(q));
  }, [products, items, search]);

  const addProduct = (p) => {
    setItems(prev => [...prev, { id: p.id, nom: p.nom, unite: p.unite, qte: 1, pu: "" }]);
    setSearch("");
    setPickerOpen(false);
  };
  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));
  const updateItem = (id, field, value) =>
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));

  const resetForm = () => {
    setFournisseur(""); setReference(""); setNote(""); setItems([]); setShowForm(false);
    setSearch(""); setPickerOpen(false);
  };

  const submit = async () => {
    if (!fournisseur || !items.length) { toast.warning("", "Fournisseur et produits requis"); return; }
    if (items.some(i => !(Number(i.qte) > 0) || Number(i.pu) < 0 || i.pu === "")) {
      toast.warning("", "Quantité et prix unitaire requis pour chaque produit"); return;
    }
    setLoading(true);
    try {
      const achat = await achatsService.create({
        fournisseur,
        reference,
        note,
        lignes: items.map(i => ({
          produit_id: i.id,
          quantite: Number(i.qte),
          prix_unitaire: Number(i.pu),
        })),
      });
      setAchats(p => [achat, ...p]);

      if (achat.statut === "VALIDEE") {
        // Stock appliqué immédiatement → rafraîchir
        productsService.list().then(d => { if (d && setProducts) setProducts(unwrap(d)); }).catch(() => {});
        movementsService.list().then(d => { if (d && setMovements) setMovements(unwrap(d)); }).catch(() => {});
        toast.success("Achat validé", `Stock mis à jour — dépense de ${fmt(achat.montant_total)} créée`);
      } else {
        toast.success("Achat enregistré", "En attente de validation par un manager");
      }

      resetForm();
    } catch (err) { handleApiError(err, toast); } finally { setLoading(false); }
  };

  /* ── Créer un produit (sans quantité initiale) ── */
  const createProduct = async () => {
    if (!prod.nom || !prod.unite_id) { toast.warning("", "Nom et unité requis"); return; }
    setLoading(true);
    try {
      const newP = await productsService.create({
        nom: prod.nom,
        categorie: prod.categorie || "Divers",
        seuil_alerte: Number(prod.seuil_alerte) || 0,
        unite_id: Number(prod.unite_id),
        date_peremption: prod.date_peremption || null,
        description: prod.description || "",
      });
      if (setProducts) setProducts(p => [...p, newP]);
      setProd(EMPTY_PROD);
      setShowProd(false);
      toast.success("Produit créé", newP.nom);
    } catch (err) { handleApiError(err, toast); } finally { setLoading(false); }
  };

  /* ── Valider / Rejeter un achat en attente (manager / admin) ── */
  const validateAchat = async (a) => {
    setLoading(true);
    try {
      const updated = await achatsService.validate(a.num_id ?? a.id);
      setAchats(prev => prev.map(x => (x.num_id ?? x.id) === (a.num_id ?? a.id) ? updated : x));
      productsService.list().then(d => { if (d && setProducts) setProducts(unwrap(d)); }).catch(() => {});
      movementsService.list().then(d => { if (d && setMovements) setMovements(unwrap(d)); }).catch(() => {});
      toast.success("Achat validé", `Stock mis à jour — dépense de ${fmt(updated.montant_total)} créée`);
    } catch (err) { handleApiError(err, toast); } finally { setLoading(false); }
  };

  const rejectAchat = async (a) => {
    if (!window.confirm(`Rejeter l'achat ${a.id} ? Il sera supprimé.`)) return;
    setLoading(true);
    try {
      await achatsService.reject(a.num_id ?? a.id, "");
      setAchats(prev => prev.filter(x => (x.num_id ?? x.id) !== (a.num_id ?? a.id)));
      toast.success("Achat rejeté", a.id);
    } catch (err) { handleApiError(err, toast); } finally { setLoading(false); }
  };

  return (
    <div style={{ padding: 28, overflowY: "auto", flex: 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: C.muted, flex: 1, minWidth: 200 }}>
          {isManagerOrAdmin
            ? "Vos achats sont validés immédiatement : le stock est mis à jour et une dépense « Achats de stock » est créée."
            : "Vos achats sont enregistrés en attente : un manager doit les valider avant mise à jour du stock."}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn variant="ghost" onClick={() => setShowProd(true)}>+ Créer un produit</Btn>
          <Btn onClick={() => setShowForm(true)}>+ Enregistrer un achat</Btn>
        </div>
      </div>

      {fetching ? <Spinner/> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {achats.map((a, i) => (
            <Card key={a.num_id ?? a.id} className="anim-fadeUp" style={{ padding: "14px 18px", animationDelay: `${i * 35}ms`, cursor: "pointer" }}>
              <div onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.mutedL }}>{a.id}</span>
                    {a.statut === "EN_ATTENTE"
                      ? <Badge color={C.warning}>En attente de validation</Badge>
                      : <Badge color={C.success}>Validé — stock à jour</Badge>}
                    {a.reference && <span style={{ fontSize: 10, color: C.muted }}>Réf. {a.reference}</span>}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.cream }}>
                    {a.fournisseur} — <span style={{ fontWeight: 400, color: C.muted }}>{a.lignes?.length ?? 0} produit(s)</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {a.cree_par} · {new Date(a.date_achat).toLocaleString("fr-FR")}
                    {a.valide_par && a.statut === "VALIDEE" && <span> · validé par {a.valide_par}</span>}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="serif" style={{ fontSize: 18, fontWeight: 700, color: C.gold }}>{fmt(a.montant_total)}</div>
                  <div style={{ fontSize: 10, color: C.muted }}>{expanded === a.id ? "▲ replier" : "▼ détail"}</div>
                </div>
              </div>

              {/* Actions de validation (manager / admin, achats en attente) */}
              {isManagerOrAdmin && a.statut === "EN_ATTENTE" && (
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}
                  onClick={(e) => e.stopPropagation()}>
                  <Btn small variant="success" loading={loading} onClick={() => validateAchat(a)}>✓ Valider</Btn>
                  <Btn small variant="danger" loading={loading} onClick={() => rejectAchat(a)}>Rejeter</Btn>
                </div>
              )}

              {expanded === a.id && (
                <div style={{ marginTop: 12, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 10 }}>
                  {(a.lignes ?? []).map(l => (
                    <div key={l.id} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 12 }}>
                      <span style={{ color: C.cream }}>
                        {l.produit_nom} <span style={{ color: C.muted }}>× {Number(l.quantite)} {l.unite}</span>
                      </span>
                      <span style={{ color: C.muted }}>
                        {fmt(l.prix_unitaire)} /u — <span style={{ color: C.cream }}>{fmt(l.montant)}</span>
                      </span>
                    </div>
                  ))}
                  {a.note && <div style={{ fontSize: 11, color: C.muted, marginTop: 6, fontStyle: "italic" }}>{a.note}</div>}
                </div>
              )}
            </Card>
          ))}
          {achats.length === 0 && <Empty icon="📥" text="Aucun achat enregistré"/>}
        </div>
      )}

      <Modal open={showForm} onClose={resetForm} title="Enregistrer un achat" width={620}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Input label="Fournisseur" value={fournisseur} onChange={setFournisseur} placeholder="Nom du fournisseur" required/>
            <Input label="Réf. facture fournisseur" value={reference} onChange={setReference} placeholder="Optionnel"/>
          </div>

          <div>
            <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>
              Produits achetés <span style={{ color: C.gold }}>*</span>
            </div>

            {/* Combobox : menu déroulant avec recherche intégrée */}
            <div style={{ position: "relative", marginBottom: items.length ? 12 : 0 }}>
              <div
                onClick={() => setPickerOpen(o => !o)}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  background: C.bg3, border: `1px solid ${pickerOpen ? C.gold : C.goldBorder}`,
                  borderRadius: 8, padding: "10px 14px", cursor: "pointer", userSelect: "none",
                }}>
                <span style={{ fontSize: 13, color: C.muted }}>
                  {availableProducts.length === 0 && !search
                    ? "Tous les produits ont été ajoutés"
                    : "Rechercher et ajouter un produit…"}
                </span>
                <span style={{ fontSize: 11, color: C.muted }}>{pickerOpen ? "▲" : "▼"}</span>
              </div>

              {pickerOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
                  background: C.bg2, border: `1px solid ${C.goldBorder}`, borderRadius: 8,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.5)", overflow: "hidden",
                }}>
                  <div style={{ padding: 8, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                    <input
                      autoFocus
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Taper pour filtrer…"
                      style={{
                        width: "100%", boxSizing: "border-box", background: C.bg4,
                        border: `1px solid ${C.goldBorder}`, borderRadius: 6,
                        padding: "8px 10px", color: C.cream, fontSize: 13, outline: "none",
                      }}/>
                  </div>
                  <div style={{ maxHeight: 220, overflowY: "auto" }}>
                    {availableProducts.length === 0 ? (
                      <div style={{ padding: "14px", fontSize: 12, color: C.muted, textAlign: "center" }}>
                        {search ? "Aucun produit trouvé" : "Aucun produit disponible"}
                      </div>
                    ) : availableProducts.map(p => (
                      <div key={p.id}
                        onClick={() => addProduct(p)}
                        style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          padding: "9px 14px", cursor: "pointer", fontSize: 13,
                          borderBottom: `1px solid rgba(255,255,255,0.04)`,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = C.bg3}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <span style={{ color: C.cream }}>{p.nom}</span>
                        <span style={{ fontSize: 11, color: C.muted }}>stock: {p.qte}{p.unite}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Produits ajoutés : quantité · prix unitaire · unité figée */}
            {items.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {items.map(it => (
                  <div key={it.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    background: C.bg3, borderRadius: 8, padding: "8px 12px", gap: 8,
                  }}>
                    <span style={{ fontSize: 13, color: C.cream, minWidth: 0, flex: 1 }} className="truncate">
                      {it.nom}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      <input type="number" value={it.qte} min="0.1" step="0.1" title="Quantité"
                        onChange={e => updateItem(it.id, "qte", e.target.value)}
                        style={{ width: 60, background: C.bg4, border: `1px solid ${C.goldBorder}`, borderRadius: 6, padding: "4px 8px", color: C.cream, fontSize: 12 }}/>
                      <span style={{ fontSize: 11, color: C.muted, minWidth: 26 }}>{it.unite}</span>
                      <span style={{ fontSize: 11, color: C.muted }}>×</span>
                      <input type="number" value={it.pu} min="0" step="1" placeholder="PU" title="Prix unitaire"
                        onChange={e => updateItem(it.id, "pu", e.target.value)}
                        style={{ width: 82, background: C.bg4, border: `1px solid ${C.goldBorder}`, borderRadius: 6, padding: "4px 8px", color: C.cream, fontSize: 12 }}/>
                      <span style={{ fontSize: 11, color: C.gold, minWidth: 70, textAlign: "right" }}>
                        {fmt((Number(it.qte) || 0) * (Number(it.pu) || 0))}
                      </span>
                      <Btn small variant="danger" onClick={() => removeItem(it.id)}>✕</Btn>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Input label="Note" value={note} onChange={setNote} placeholder="Commentaire (optionnel)" textarea/>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: C.bg3, borderRadius: 8, padding: "10px 14px" }}>
            <span style={{ fontSize: 11, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase" }}>Total de l'achat</span>
            <span className="serif" style={{ fontSize: 20, fontWeight: 700, color: C.gold }}>{fmt(total)}</span>
          </div>

          <div style={{ fontSize: 11, color: C.muted }}>
            ⚡ Effet immédiat : le stock sera mis à jour et une dépense «&nbsp;Achats de stock&nbsp;» de {fmt(total)} sera créée.
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={resetForm}>Annuler</Btn>
            <Btn loading={loading} onClick={submit} disabled={!fournisseur || !items.length}>Enregistrer l'achat</Btn>
          </div>
        </div>
      </Modal>

      {/* Modale : Créer un produit (sans quantité initiale) */}
      <Modal open={showProd} onClose={() => { setShowProd(false); setProd(EMPTY_PROD); }}
        title="Créer un produit" width={560}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ gridColumn: "1/-1" }}>
            <Input label="Nom du produit *" value={prod.nom} onChange={v => pf({ nom: v })} required/>
          </div>
          <Input label="Catégorie" value={prod.categorie} onChange={v => pf({ categorie: v })} placeholder="Ex: Viandes"/>
          <div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Unité *</div>
            <select value={prod.unite_id} onChange={e => pf({ unite_id: e.target.value })}
              style={{ width: "100%", background: C.bg2, border: `1px solid rgba(255,255,255,0.08)`,
                borderRadius: 8, padding: "9px 12px", color: prod.unite_id ? C.cream : C.muted,
                fontSize: 13, fontFamily: "'Raleway',sans-serif", outline: "none", cursor: "pointer" }}>
              <option value="">-- Sélectionner une unité --</option>
              {unites.map(u => <option key={u.id} value={u.id}>{u.nom} ({u.abreviation})</option>)}
            </select>
          </div>
          <Input label="Seuil d'alerte" type="number" value={prod.seuil_alerte} onChange={v => pf({ seuil_alerte: v })} min="0"/>
          <div style={{ gridColumn: "1/-1" }}>
            <Input label="Date de péremption" type="date" value={prod.date_peremption} onChange={v => pf({ date_peremption: v })}/>
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <Input label="Description" value={prod.description} onChange={v => pf({ description: v })} placeholder="Optionnel…"/>
          </div>
          <div style={{ gridColumn: "1/-1", fontSize: 11, color: C.muted }}>
            Le produit démarre à 0 en stock. La quantité augmentera via les achats validés.
          </div>
          <div style={{ gridColumn: "1/-1", display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => { setShowProd(false); setProd(EMPTY_PROD); }}>Annuler</Btn>
            <Btn loading={loading} onClick={createProduct} disabled={!prod.nom || !prod.unite_id}>Créer le produit</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StockEntriesScreen;
